import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const footerCategoryLinks = categories.slice(0, 7);
const footerSocialLinks = [
  { name: 'X (Twitter)', url: 'https://kaspreadsheet.com/', icon: 'ri-twitter-x-line' },
  { name: 'Instagram', url: 'https://kaspreadsheet.com/', icon: 'ri-instagram-line' },
  { name: 'Pinterest', url: 'https://kaspreadsheet.com/', icon: 'ri-pinterest-line' },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || submitStatus === 'loading') return;
    setSubmitStatus('loading');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email.trim());
      await fetch('https://readdy.ai/api/form/d8s0db3f81iibef4bimg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitStatus('success');
      setEmail('');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } catch {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  return (
    <footer className="bg-background-950">
      <div className="w-full px-4 md:px-6 lg:px-12 py-14 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          <div>
            <Link to="/" className="cursor-pointer">
              <span className="font-heading text-2xl font-bold tracking-tight text-background-50">
                OOPBuy
              </span>
            </Link>
            <p className="mt-4 text-sm text-foreground-300 leading-relaxed max-w-xs">
              OOPBuy Spreadsheet is a structured product sourcing and comparison system designed to help users analyze products efficiently using a spreadsheet-based workflow.
            </p>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-5">
              Subscribe
            </h4>
            <form
              onSubmit={handleSubscribe}
              data-readdy-form
              className="relative"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                required
                className="w-full bg-transparent border-b border-foreground-600 py-2.5 pr-10 text-sm text-background-50 placeholder:text-foreground-500 focus:outline-none focus:border-accent-400 transition-colors"
              />
              <button
                type="submit"
                disabled={submitStatus === 'loading'}
                className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-background-50 transition-colors cursor-pointer disabled:opacity-50"
              >
                {submitStatus === 'loading' ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <i className="ri-arrow-right-line"></i>
                )}
              </button>
            </form>
            {submitStatus === 'success' && (
              <p className="mt-2 text-xs text-accent-400">Subscribed successfully!</p>
            )}
            {submitStatus === 'error' && (
              <p className="mt-2 text-xs text-red-400">Something went wrong. Try again.</p>
            )}
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-5">
              Categories
            </h4>
            <ul className="space-y-2.5">
              {footerCategoryLinks.map((cat) => (
                <li key={cat.id}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-5">
              Social
            </h4>
            <ul className="space-y-2.5">
              {footerSocialLinks.map((social) => (
                <li key={social.name}>
                  <a
                    href={social.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={social.icon}></i>
                    </span>
                    {social.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-foreground-800/40 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} OOPBuy. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="text-xs text-foreground-500 hover:text-background-50 transition-colors cursor-pointer"
            >
              Main Platform
            </a>
            <Link
              to="/what-is-oopbuy-spreadsheet"
              className="text-xs text-foreground-500 hover:text-background-50 transition-colors cursor-pointer"
            >
              SEO Guide
            </Link>
            <Link
              to="/"
              className="text-xs text-foreground-500 hover:text-background-50 transition-colors cursor-pointer"
            >
              Home
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}